/**
 * Analytics — Reports API Contracts
 *
 * Wire format for ReportsController endpoints.
 */

import { z } from 'zod';

const REPORT_FORMATS = ['CSV', 'PDF', 'EXCEL'] as const;
const REPORT_FREQUENCIES = ['DAILY', 'WEEKLY', 'MONTHLY', 'CUSTOM'] as const;

// ---------------------------------------------------------------------------
// Request schemas
// ---------------------------------------------------------------------------

export const ReportParamsSchema = z.object({
  startDate: z.string(),
  endDate: z.string(),
});
export type ReportParams = z.infer<typeof ReportParamsSchema>;

export const CreateReportRequestSchema = z.object({
  name: z.string().min(1, 'Report name is required.'),
  type: z.string().min(1, 'Report type is required.'),
  schedule: z.string().optional(),
  frequency: z.enum(REPORT_FREQUENCIES).default('WEEKLY'),
  format: z.enum(REPORT_FORMATS).default('CSV'),
  recipients: z.array(z.string().email('Enter a valid email address.')).min(1, 'At least one recipient is required.'),
  description: z.string().optional(),
  parameters: ReportParamsSchema.optional(),
  isActive: z.boolean().default(true),
  createdBy: z.string().uuid().optional(),
}).superRefine((data, ctx) => {
  if (data.frequency === 'CUSTOM' && !data.schedule?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'A cron expression is required for custom frequency.',
      path: ['schedule'],
    });
  }
});
export type CreateReportRequest = z.infer<typeof CreateReportRequestSchema>;

export const UpdateReportRequestSchema = z.object({
  name: z.string().min(1).optional(),
  schedule: z.string().optional(),
  frequency: z.enum(REPORT_FREQUENCIES).optional(),
  format: z.enum(REPORT_FORMATS).optional(),
  recipients: z.array(z.string().email()).optional(),
  description: z.string().optional(),
  parameters: ReportParamsSchema.optional(),
  isActive: z.boolean().optional(),
});
export type UpdateReportRequest = z.infer<typeof UpdateReportRequestSchema>;

export const RunReportRequestSchema = z.object({
  parameters: ReportParamsSchema.optional(),
  triggeredBy: z.string().optional(),
});
export type RunReportRequest = z.infer<typeof RunReportRequestSchema>;

export const ListReportsQuerySchema = z.object({
  type: z.string().optional(),
  isActive: z.enum(['true', 'false']).optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});
export type ListReportsQuery = z.infer<typeof ListReportsQuerySchema>;

// ---------------------------------------------------------------------------
// Response schemas
// ---------------------------------------------------------------------------

export const ReportScheduleConfigItemSchema = z.object({
  type: z.string(),
  name: z.string(),
  description: z.string(),
  supportedFormats: z.array(z.string()),
  defaultSchedule: z.string(),
});
export type ReportScheduleConfigItem = z.infer<typeof ReportScheduleConfigItemSchema>;

export const ReportRecordSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string(),
  schedule: z.string().nullable().optional(),
  frequency: z.string().nullable().optional(),
  format: z.enum(['CSV', 'PDF', 'EXCEL']),
  recipients: z.array(z.string()),
  description: z.string().nullable().optional(),
  parameters: z.unknown(), // Prisma stores as JsonValue; consumers should validate the specific shape they need
  isActive: z.boolean(),
  createdBy: z.string().nullable().optional(),
  nextRunAt: z.string().or(z.date()).nullable().optional(),
  lastRunAt: z.string().or(z.date()).nullable().optional(),
  createdAt: z.string().or(z.date()).optional(),
  updatedAt: z.string().or(z.date()).optional(),
});
export type ReportRecord = z.infer<typeof ReportRecordSchema>;

export const ReportExecutionSchema = z.object({
  id: z.string(),
  reportId: z.string(),
  status: z.string(), // 'running' | 'completed' | 'failed' — constrained by app logic, stored as string in DB
  parameters: z.unknown(), // Prisma stores as JsonValue; consumers should validate the specific shape they need
  startedAt: z.string().or(z.date()),
  triggeredBy: z.string().nullable().optional(),
  completedAt: z.string().or(z.date()).nullable().optional(),
  durationMs: z.number().nullable().optional(),
  rowCount: z.number().int().nullable().optional(),
  resultUrl: z.string().nullable().optional(),
  resultFormat: z.string().nullable().optional(),
  errorMessage: z.string().nullable().optional(),
});
export type ReportExecution = z.infer<typeof ReportExecutionSchema>;

export const ListReportsResponseSchema = z.object({
  data: z.array(ReportRecordSchema),
  meta: z.object({
    total: z.number().int(),
    page: z.number().int(),
    limit: z.number().int(),
    totalPages: z.number().int(),
  }),
});
export type ListReportsResponse = z.infer<typeof ListReportsResponseSchema>;

export const ListExecutionsResponseSchema = z.object({
  data: z.array(ReportExecutionSchema),
  meta: z.object({
    total: z.number().int(),
    page: z.number().int(),
    limit: z.number().int(),
    totalPages: z.number().int(),
  }),
});
export type ListExecutionsResponse = z.infer<typeof ListExecutionsResponseSchema>;

export const RunReportResponseSchema = z.object({
  executionId: z.string(),
  status: z.literal('running'),
  message: z.string(),
});
export type RunReportResponse = z.infer<typeof RunReportResponseSchema>;
